// Math domain guide. Each section explains how to recognize a question type,
// walks through the method for solving it, and includes at least one fully
// worked example. Paragraphs are plain strings, callout text objects
// { text, label, kind }, or structured blocks { type: 'steps' | 'list', ... }
// — steps/list blocks render as a numbered or bulleted list instead of one
// run-on sentence. kind is 'example', 'tip', or 'mistake' and controls the
// callout badge color in StudyGuide.css.

export const documentTitle = 'Math';
export const documentSlug = 'math';

export const parts = [
  {
    id: 'part-math-approach',
    title: 'How to Approach the Math Section',
    sections: [
      {
        id: 'math-how-it-works',
        heading: 'How the Math Section Works',
        paragraphs: [
          'The Math section has two modules of 22 questions each (44 questions total), with 35 minutes per module — about 95 seconds per question on average, though you should move faster on questions you find easy to bank extra time for harder ones. About 75% of questions are multiple choice with four answer choices; the rest are student-produced response ("grid-in") questions where you type in your own numeric answer instead of choosing from a list.',
          'The section is adaptive at the module level: how you perform on module 1 determines whether module 2 draws from a harder or easier question pool. There is a full graphing calculator (Desmos) built into the testing app for every math question — you never need to switch between calculator and no-calculator sections like on the old paper SAT.',
        ],
      },
      {
        id: 'math-when-unsure',
        heading: "Strategies for When You're Not Sure",
        paragraphs: [
          'Before you guess randomly, try one of these fallback techniques — they turn "I don\'t know how to solve this algebraically" into "I can still figure out which answer is correct."',
          {
            kind: 'tip',
            label: 'Strategy Tip',
            text: 'Backsolve from the answer choices. If a question describes a situation and asks for a value, and you\'re stuck setting up the equation, plug each answer choice into the problem instead and see which one makes everything true. Start with a middle answer choice (like B or C) — if it makes the result too big or too small, you immediately know which direction to try next, often letting you skip testing the remaining choices.',
          },
          {
            kind: 'tip',
            label: 'Strategy Tip',
            text: 'Plug in your own numbers. For questions that ask which expression is "equivalent to" another, or how a change in one variable affects another, pick simple numbers (like 2 or 3) that satisfy any constraints given, compute the actual numeric answer, and then test which answer choice produces that same number. This turns an abstract algebra question into simple arithmetic.',
          },
          {
            kind: 'tip',
            label: 'Strategy Tip',
            text: 'Use the built-in Desmos calculator to check your work, not just to compute. You can graph both sides of an equation and read off the intersection point as the solution to a system, graph a function to check its vertex or intercepts, or type in an expression to confirm an algebraic simplification. It\'s available on every question, so use it whenever typing something in is faster than working it out by hand.',
          },
          {
            kind: 'tip',
            label: 'Strategy Tip',
            text: "Eliminate before you guess, and never leave a question blank. There is no penalty for a wrong answer, so a blank question and a wrong guess score exactly the same — zero — while a guess always has a chance of being right. Rule out any answer choice you can disprove for a specific reason (wrong units, wrong sign, doesn't satisfy a constraint given in the problem) before choosing among what's left.",
          },
        ],
      },
      {
        id: 'math-grid-in',
        heading: 'Student-Produced Response ("Grid-In") Questions',
        paragraphs: [
          'About a quarter of math questions give you no answer choices — you type your own numeric answer into a box. A few formatting rules matter here: grid-in answers can\'t be negative (if your algebra produces a negative number for one of these, double check your work, since the format won\'t accept it), and if your answer is a fraction that doesn\'t fit, you can enter its decimal equivalent instead. If a question has more than one correct answer, you only need to enter one of them.',
          'Because there are no answer choices to sanity-check against, it\'s worth taking a few extra seconds on grid-in questions to re-read the problem and confirm your answer actually answers what was asked (for example, that you solved for the right variable, or reported a total instead of just one part of it).',
        ],
      },
    ],
  },
  {
    id: 'part-algebra',
    title: 'Algebra',
    sections: [
      {
        id: 'math-linear-one-var',
        heading: 'Linear Equations and Inequalities in One Variable',
        paragraphs: [
          'These questions ask you to solve, or interpret the solution to, an equation or inequality with a single variable raised only to the first power. Isolate the variable methodically:',
          {
            type: 'list',
            items: [
              'Distribute any parentheses first.',
              'Combine like terms on each side.',
              'Undo addition/subtraction before multiplication/division.',
              'Check your answer by substituting it back into the original equation.',
            ],
          },
          {
            kind: 'example',
            label: 'Worked Example',
            type: 'steps',
            intro: 'Solve for x: 3(2x - 4) = 5x + 7.',
            steps: [
              'Distribute the 3: 6x - 12 = 5x + 7.',
              'Subtract 5x from both sides: x - 12 = 7.',
              'Add 12 to both sides: x = 19.',
              'Check: 3(2(19) - 4) = 3(34) = 102, and 5(19) + 7 = 95 + 7 = 102 — both sides match, so x = 19 is correct.',
            ],
          },
          {
            kind: 'mistake',
            label: 'Common Mistake',
            text: 'When you multiply or divide both sides of an inequality by a negative number, you must flip the inequality sign. For example, solving -2x + 5 ≤ 13 gives -2x ≤ 8, and dividing both sides by -2 flips the sign to give x ≥ -4. Forgetting this flip is one of the most common algebra errors on the exam.',
          },
        ],
      },
      {
        id: 'math-linear-two-var',
        heading: 'Linear Functions in Two Variables',
        paragraphs: [
          'This covers the equation of a line — most often slope-intercept form, y = mx + b, where m is the slope and b is the y-intercept — and interpreting slope and intercepts in context. Slope is the rate of change: rise over run, or (y₂ - y₁) / (x₂ - x₁) between any two points on the line. In a word problem, the slope is usually a rate ("dollars per hour," "feet per second") and the y-intercept is usually a starting value.',
          {
            kind: 'example',
            label: 'Worked Example',
            type: 'steps',
            intro: 'A line passes through (2, 5) and (6, 17). Find its equation.',
            steps: [
              'Find the slope: (17 - 5) / (6 - 2) = 12 / 4 = 3.',
              'Use point-slope form with (2, 5): y - 5 = 3(x - 2).',
              'Simplify: y = 3x - 1.',
              'Check with the other point: 3(6) - 1 = 17. Correct.',
            ],
          },
          {
            kind: 'example',
            label: 'Worked Example',
            text: 'A moving company charges a flat fee of $25 plus $4 per hour of labor. If y is the total cost and x is the number of hours, the equation is y = 4x + 25 — the $4 rate is the slope (cost per hour), and the $25 flat fee is the y-intercept (the cost when x = 0 hours).',
          },
        ],
      },
      {
        id: 'math-systems',
        heading: 'Systems of Two Linear Equations',
        paragraphs: [
          'This covers solving a system of two linear equations by substitution or elimination, and interpreting how many solutions a system has:',
          {
            type: 'list',
            items: [
              "Two different lines that aren't parallel intersect at exactly one point (one solution).",
              'Parallel lines (same slope, different intercept) never intersect (no solution).',
              'Two equations describing the exact same line have infinitely many solutions — check whether one equation is just a multiple of the other.',
            ],
          },
          {
            kind: 'example',
            label: 'Worked Example',
            type: 'steps',
            intro: 'Solve: 2x + 3y = 12 and x - y = 1.',
            steps: [
              'From the second equation, x = y + 1.',
              'Substitute into the first equation: 2(y + 1) + 3y = 12.',
              'Simplify: 2y + 2 + 3y = 12, so 5y = 10, so y = 2.',
              'Substitute back: x = 2 + 1 = 3.',
              'Check both equations: 2(3) + 3(2) = 12 ✓, and 3 - 2 = 1 ✓.',
            ],
          },
          {
            kind: 'mistake',
            label: 'Common Mistake',
            text: 'When using elimination, forgetting to multiply every term on both sides of an equation (not just the variable you\'re trying to cancel) is the most common error — if you multiply one equation by -2 to eliminate a variable, the constant on the right side needs to be multiplied by -2 as well.',
          },
        ],
      },
      {
        id: 'math-inequalities-two-var',
        heading: 'Linear Inequalities and Systems of Inequalities in Two Variables',
        paragraphs: [
          'A linear inequality in two variables, like y > 2x - 3, describes a region of the coordinate plane rather than a single line. Graph the boundary line first (dashed for strict inequalities < or >, solid for ≤ or ≥), then determine which side to shade by testing a point not on the line — the origin (0, 0) is usually the easiest choice, as long as the line doesn\'t pass through it.',
          {
            kind: 'example',
            label: 'Worked Example',
            type: 'steps',
            intro: 'For y > 2x - 3, determine which side of the boundary line to shade.',
            steps: [
              'Test the origin (0, 0): is 0 > 2(0) - 3, i.e., is 0 > -3?',
              'Yes — so shade the side of the boundary line that contains the origin.',
              'For a system of two inequalities, graph both and shade the region where both shaded areas overlap; any point there satisfies both inequalities at once.',
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'part-advanced-math',
    title: 'Advanced Math',
    sections: [
      {
        id: 'math-factoring',
        heading: 'Equivalent Expressions and Factoring',
        paragraphs: [
          'This covers rewriting expressions in equivalent forms: factoring quadratics, simplifying rational expressions, and applying exponent rules. To factor a quadratic x² + bx + c, look for two numbers that multiply to c and add to b. To simplify a rational (fraction) expression, factor the numerator and denominator completely first, then cancel any factor that appears in both — never cancel a term that\'s only added or subtracted, only factors that are multiplied.',
          {
            kind: 'example',
            label: 'Worked Example',
            type: 'steps',
            intro: 'Factor x² - 5x - 14.',
            steps: [
              'Find two numbers that multiply to -14 and add to -5: those are -7 and 2.',
              'So x² - 5x - 14 = (x - 7)(x + 2).',
              'Check by expanding: x² + 2x - 7x - 14 = x² - 5x - 14. Correct.',
            ],
          },
          {
            kind: 'example',
            label: 'Worked Example',
            type: 'steps',
            intro: 'Simplify (x² - 9) / (x² - x - 6).',
            steps: [
              'Factor the numerator: x² - 9 = (x - 3)(x + 3).',
              'Factor the denominator: x² - x - 6 = (x - 3)(x + 2).',
              'Cancel the shared (x - 3) factor: (x + 3) / (x + 2).',
              'Note the original expression is undefined at x = 3 and x = -2.',
            ],
          },
        ],
      },
      {
        id: 'math-nonlinear-equations',
        heading: 'Nonlinear Equations in One Variable and Systems',
        paragraphs: [
          'This covers solving quadratic and other nonlinear equations, and systems that mix a linear and a nonlinear equation. Try factoring first; if a quadratic doesn\'t factor cleanly, use the quadratic formula: for ax² + bx + c = 0, x = (-b ± √(b² - 4ac)) / (2a). The expression under the square root (the discriminant) tells you how many real solutions exist: positive means two, zero means exactly one, and negative means none.',
          {
            kind: 'example',
            label: 'Worked Example',
            type: 'steps',
            intro: 'Solve x² - 2x - 15 = 0 by factoring.',
            steps: ['Factor: (x - 5)(x + 3) = 0.', 'So x = 5 or x = -3.'],
          },
          {
            kind: 'example',
            label: 'Worked Example',
            type: 'steps',
            intro: "Solve x² + 4x + 1 = 0 using the quadratic formula (it doesn't factor nicely).",
            steps: [
              'Identify a = 1, b = 4, c = 1.',
              'Substitute into the formula: x = (-4 ± √(16 - 4)) / 2.',
              'Simplify: x = (-4 ± √12) / 2 = -2 ± √3.',
            ],
          },
          {
            kind: 'example',
            label: 'Worked Example',
            type: 'steps',
            intro: 'Solve the system y = x + 1 and y = x² - 1 by substitution.',
            steps: [
              'Set the expressions equal: x + 1 = x² - 1.',
              'Rearrange: x² - x - 2 = 0.',
              'Factor: (x - 2)(x + 1) = 0, so x = 2 or x = -1.',
              'Substitute back: x = 2 gives y = 3, and x = -1 gives y = 0.',
              'This system has two solutions — the two points where the line and the parabola intersect.',
            ],
          },
        ],
      },
      {
        id: 'math-nonlinear-functions',
        heading: 'Nonlinear Functions',
        paragraphs: [
          'This covers function notation and reading the key features of nonlinear graphs:',
          {
            type: 'list',
            items: [
              'f(a) means "plug a in for x and evaluate."',
              'Vertex form of a quadratic: y = a(x - h)² + k, with vertex (h, k) — a minimum if a is positive, a maximum if a is negative.',
              'Exponential growth/decay: y = a(1 ± r)ᵗ, where a is the starting amount, r is the rate as a decimal, and t is the number of time periods.',
            ],
          },
          'The key difference from linear growth: exponential change multiplies by a fixed factor each period rather than adding a fixed amount.',
          {
            kind: 'example',
            label: 'Worked Example',
            text: 'For y = 2(x - 3)² + 5, the vertex is at (3, 5), and since a = 2 is positive, this is the minimum value of the function.',
          },
          {
            kind: 'example',
            label: 'Worked Example',
            text: 'A population of 200 grows 5% per year. After t years, the population is y = 200(1.05)ᵗ. After 3 years: 200(1.05)³ = 200(1.157625) ≈ 231.5.',
          },
        ],
      },
    ],
  },
  {
    id: 'part-psda',
    title: 'Problem-Solving and Data Analysis',
    sections: [
      {
        id: 'math-ratios-rates',
        heading: 'Ratios, Rates, Proportions, and Units',
        paragraphs: [
          'These questions apply proportional reasoning to real-world scenarios. Set up a proportion with matching units in the same position on each side (for example, cups over loaves on both sides of the equation), then cross-multiply and solve. For unit conversions, multiply by a conversion factor written as a fraction so the unwanted unit cancels out.',
          {
            kind: 'example',
            label: 'Worked Example',
            text: 'A recipe uses 3 cups of flour for 2 loaves of bread. How much flour is needed for 5 loaves? Set up 3/2 = x/5, so x = (3 × 5) / 2 = 7.5 cups.',
          },
          {
            kind: 'example',
            label: 'Worked Example',
            text: 'Convert 45 miles per hour to feet per second: 45 mi/hr × 5,280 ft/mi × 1 hr/3,600 s = (45 × 5,280) / 3,600 = 66 feet per second. Notice that "miles" and "hours" cancel, leaving only feet and seconds.',
          },
        ],
      },
      {
        id: 'math-percentages',
        heading: 'Percentages',
        paragraphs: [
          'Percent change is calculated as (new value - old value) / old value × 100. To increase a value by a percent, multiply by (1 + the rate); to decrease, multiply by (1 - the rate). When a value changes by one percentage more than once, each change applies to the new, already-changed value — not the original — so successive percent changes don\'t simply add together.',
          {
            kind: 'example',
            label: 'Worked Example',
            type: 'steps',
            intro: 'A $80 item increases 15% and then decreases 10%. Find the final price.',
            steps: [
              'After the 15% increase: 80 × 1.15 = $92.',
              'After the 10% decrease: 92 × 0.90 = $82.80.',
              "The net change is only a 3.5% increase from the original $80 — not the 5% you'd get from naively subtracting 10% from 15%.",
            ],
          },
          {
            kind: 'mistake',
            label: 'Common Mistake',
            text: "Assuming a percent increase followed by a percent decrease (or vice versa) cancels out to the difference between the two rates. It doesn't, because the second percentage is taken of a different base amount than the first.",
          },
        ],
      },
      {
        id: 'math-one-var-data',
        heading: 'One-Variable Data: Distributions and Statistics',
        paragraphs: [
          'This covers mean, median, mode, and how the shape of a distribution affects them. The mean (average) is pulled toward extreme values (outliers), while the median (the middle value when data is sorted) is not — this is why median is often a more representative "typical value" for a data set that has outliers.',
          {
            kind: 'example',
            label: 'Worked Example',
            type: 'list',
            intro: 'For the data set {2, 3, 3, 5, 50}:',
            items: ['Mean = (2 + 3 + 3 + 5 + 50) / 5 = 63 / 5 = 12.6', 'Median (the middle value once sorted) = 3'],
          },
          'The single outlier of 50 drags the mean far above where most of the data actually is, while the median stays representative of the typical value.',
        ],
      },
      {
        id: 'math-two-var-data',
        heading: 'Two-Variable Data: Scatterplots and Models',
        paragraphs: [
          'These questions show a scatterplot with a line or curve of best fit and ask you to read a value from it, describe the correlation (positive, negative, or none), or interpret what the slope means in context. To use a line of best fit given as an equation, just substitute the given x-value and solve for y (or vice versa).',
          {
            kind: 'example',
            label: 'Worked Example',
            text: 'A line of best fit is given as ŷ = 2.5x + 10. To estimate y when x = 8: ŷ = 2.5(8) + 10 = 20 + 10 = 30.',
          },
        ],
      },
      {
        id: 'math-probability',
        heading: 'Probability',
        paragraphs: [
          'Basic probability is favorable outcomes divided by total outcomes. Many probability questions give you a two-way frequency table and ask for the probability of a specific category, or a category given another has already happened (conditional probability) — for these, just divide the relevant cell (or row/column subtotal) by the appropriate total from the table.',
          {
            kind: 'example',
            label: 'Worked Example',
            text: 'A bag contains 5 red, 3 blue, and 2 green marbles (10 total). The probability of drawing a blue marble at random is 3/10, or 0.3.',
          },
        ],
      },
      {
        id: 'math-inference',
        heading: 'Inference from Sample Statistics and Margin of Error',
        paragraphs: [
          'These questions test whether you understand that a statistic calculated from a sample (like a survey) is an estimate of the true value for the whole population, not the exact value. The margin of error describes the range within which the true population value likely falls. A larger, more representative sample generally produces a smaller margin of error — a more precise estimate.',
        ],
      },
      {
        id: 'math-study-design',
        heading: 'Evaluating Statistical Claims and Study Design',
        paragraphs: [
          'This covers the difference between an observational study, which can only show that two things are associated, and a randomized controlled experiment, in which participants are randomly assigned to groups, which can support a claim of cause and effect. Random sampling supports generalizing results to a larger population; random assignment (in an experiment) supports concluding that the treatment caused the observed difference.',
        ],
      },
    ],
  },
  {
    id: 'part-geometry',
    title: 'Geometry and Trigonometry',
    sections: [
      {
        id: 'math-area-volume',
        heading: 'Area, Volume, and Angles',
        paragraphs: [
          'This covers area and volume formulas for common shapes, along with angle relationships.',
          {
            type: 'list',
            items: [
              'Rectangle area: length × width',
              'Triangle area: ½ × base × height',
              'Circle area: πr²; circumference: 2πr',
              'Rectangular prism volume: length × width × height',
              'Cylinder volume: πr²h',
            ],
          },
          {
            type: 'list',
            intro: 'Angle relationships:',
            items: [
              'Vertical angles (formed by two crossing lines) are always equal.',
              'Supplementary angles sum to 180°.',
              'When a transversal crosses two parallel lines, alternate interior angles are equal and same-side (co-interior) angles are supplementary.',
              "A triangle's interior angles always sum to 180°.",
            ],
          },
          {
            kind: 'example',
            label: 'Worked Example',
            text: 'A cylinder has radius 3 and height 10. Its volume is πr²h = π(3²)(10) = 90π ≈ 282.7.',
          },
          {
            kind: 'example',
            label: 'Worked Example',
            type: 'list',
            intro: 'Two parallel lines are cut by a transversal, forming an angle of 65° at one intersection.',
            items: [
              'The alternate interior angle at the other intersection is also 65° (alternate interior angles are congruent).',
              'The co-interior angle on the same side is 180° - 65° = 115° (co-interior angles are supplementary).',
            ],
          },
        ],
      },
      {
        id: 'math-triangles-trig',
        heading: 'Triangles and Trigonometric Ratios',
        paragraphs: [
          'This covers triangle similarity (same shape, proportional sides) and congruence (identical), along with right-triangle relationships.',
          {
            type: 'list',
            items: [
              'Pythagorean theorem: a² + b² = c² (for a right triangle\'s legs a, b and hypotenuse c)',
              'Trig ratios, remembered with SOH-CAH-TOA: sine = opposite/hypotenuse, cosine = adjacent/hypotenuse, tangent = opposite/adjacent',
              '45-45-90 triangle: legs of length x, hypotenuse x√2',
              '30-60-90 triangle: short leg x, long leg x√3, hypotenuse 2x',
            ],
          },
          {
            kind: 'example',
            label: 'Worked Example',
            text: 'A right triangle has legs of 6 and 8. Its hypotenuse is √(6² + 8²) = √(36 + 64) = √100 = 10 — the well-known 6-8-10 triangle.',
          },
          {
            kind: 'example',
            label: 'Worked Example',
            text: 'A right triangle has a 30° angle and a hypotenuse of 12. The side opposite the 30° angle is 12 × sin(30°) = 12 × 0.5 = 6.',
          },
        ],
      },
      {
        id: 'math-circles',
        heading: 'Circles',
        paragraphs: [
          'This covers the equation of a circle, along with arc length and sector area.',
          {
            type: 'list',
            items: [
              'Circle equation: (x - h)² + (y - k)² = r², where (h, k) is the center and r is the radius',
              'Arc length: (central angle / 360°) × 2πr',
              'Sector area: (central angle / 360°) × πr²',
            ],
          },
          {
            kind: 'example',
            label: 'Worked Example',
            text: 'The equation (x - 2)² + (y + 3)² = 25 describes a circle with center (2, -3) and radius √25 = 5.',
          },
          {
            kind: 'example',
            label: 'Worked Example',
            text: 'A circle has radius 6. The length of an arc with a 60° central angle is (60/360) × 2π(6) = (1/6)(12π) = 2π ≈ 6.28.',
          },
        ],
      },
    ],
  },
];
